import { POLL, REQUESTS_URI, REQUEST_URI } from '../config';

const urlBase = () => globalThis.location?.origin || 'http://localhost';

const withRequestIdQuery = (rawUrl, requestId) => {
  const url = new URL(rawUrl, urlBase());
  url.searchParams.set('requestId', requestId);
  return url.toString();
};

const withRequestIdPath = (rawUrl, requestId) => {
  const url = new URL(rawUrl, urlBase());
  url.pathname = `${url.pathname.replace(/\/+$/, '')}/${encodeURIComponent(
    requestId
  )}`;
  return url.toString();
};

export const createRequestId = () => {
  if (!globalThis.crypto?.randomUUID) {
    throw new Error('secure_random_uuid_unavailable');
  }
  return globalThis.crypto.randomUUID();
};

export async function createVerificationRequest({
  requestId = createRequestId(),
  requestsUrl = REQUESTS_URI,
  requestObjectUrl = REQUEST_URI,
  statusBaseUrl = POLL.STATUS_URL,
  signal,
} = {}) {
  if (String(statusBaseUrl).startsWith('mock:')) {
    return {
      requestId,
      requestUri: withRequestIdQuery(requestObjectUrl, requestId),
      statusUrl: statusBaseUrl,
    };
  }

  const response = await fetch(requestsUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ requestId }),
    credentials: 'omit',
    signal,
  });

  if (!response.ok) {
    throw new Error(`verification_request_creation_failed:${response.status}`);
  }

  const data = await response.json();
  if (!data?.requestId) {
    throw new Error('verification_request_id_missing');
  }
  if (data.requestId !== requestId) {
    throw new Error('verification_request_id_mismatch');
  }

  return {
    requestId: data.requestId,
    requestUri: withRequestIdQuery(requestObjectUrl, data.requestId),
    statusUrl: withRequestIdPath(statusBaseUrl, data.requestId),
  };
}

export async function getVerificationStatus({
  requestId,
  statusBaseUrl = POLL.STATUS_URL,
  signal,
} = {}) {
  if (!requestId) {
    throw new Error('verification_request_id_missing');
  }
  if (String(statusBaseUrl).startsWith('mock:')) {
    throw new Error('verification_status_unavailable');
  }

  const response = await fetch(withRequestIdPath(statusBaseUrl, requestId), {
    credentials: 'omit',
    signal,
  });

  if (!response.ok) {
    throw new Error(`verification_status_http_${response.status}`);
  }

  const contentType = response.headers?.get?.('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error('invalid_verification_status_response');
  }

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error('invalid_verification_status_response');
  }

  if (data?.requestId !== requestId) {
    throw new Error('verification_request_id_mismatch');
  }

  return data;
}
